"use client";

import { useEffect, useRef } from "react";
import * as THREE from "three";
import { useTheme } from "../../Utils/useTheme";

export default function SupplyChainNetwork3D() {
  const containerRef = useRef(null);
  const { theme } = useTheme();

  const isGreen = theme === "green";
  const isLight = theme === "light";

  const orangeColor = isGreen ? 0x10b981 : (isLight ? 0xea580c : 0xf97316);
  const amberColor = isGreen ? 0x059669 : (isLight ? 0xd97706 : 0xf59e0b);
  const goldColor = isGreen ? 0x34d399 : (isLight ? 0xca8a04 : 0xfbbf24);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const width = rect.width || 500;
    const height = rect.height || 500;

    // 1. Scene Setup
    const scene = new THREE.Scene();

    // 2. Camera Setup
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(0, 0, 15);

    // 3. Renderer Setup
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setSize(width, height, false);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.domElement.style.position = "absolute";
    renderer.domElement.style.inset = "0";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    container.appendChild(renderer.domElement);

    // 4. Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(orangeColor, 2.0); // Orange Light
    dirLight.position.set(5, 10, 7);
    scene.add(dirLight);

    const pointLight = new THREE.PointLight(goldColor, 2, 20); // Gold Light
    pointLight.position.set(0, 0, 0);
    scene.add(pointLight);

    // 5. Network Nodes Setup
    const networkGroup = new THREE.Group();
    scene.add(networkGroup);

    // A. Central Control Tower (Glowing golden octahedron)
    const towerGeo = new THREE.OctahedronGeometry(1.2, 1);
    const towerMat = new THREE.MeshPhongMaterial({
      color: orangeColor,
      emissive: goldColor,
      emissiveIntensity: 0.5,
      shininess: 100,
      wireframe: true,
    });
    const controlTower = new THREE.Mesh(towerGeo, towerMat);
    networkGroup.add(controlTower);

    // B. Outer nodes (Warehouses, Suppliers)
    const nodes = [];
    const nodeCoords = [];
    const nodeCount = 10;
    const nodeColors = [orangeColor, amberColor, goldColor];
    const geometries = [
      new THREE.BoxGeometry(0.4, 0.4, 0.4),
      new THREE.IcosahedronGeometry(0.3, 0),
      new THREE.CylinderGeometry(0.2, 0.2, 0.5, 6),
    ];

    for (let i = 0; i < nodeCount; i++) {
      const radius = 4 + Math.random() * 2;
      const theta = (i / nodeCount) * Math.PI * 2 + Math.random() * 0.5;
      const phi = (Math.random() - 0.5) * Math.PI * 0.5;

      const pos = new THREE.Vector3(
        radius * Math.cos(theta) * Math.cos(phi),
        radius * Math.sin(phi),
        radius * Math.sin(theta) * Math.cos(phi)
      );
      nodeCoords.push(pos);

      const geo = geometries[i % geometries.length];
      const mat = new THREE.MeshPhongMaterial({
        color: nodeColors[i % nodeColors.length],
        emissive: nodeColors[i % nodeColors.length],
        emissiveIntensity: 0.3,
        shininess: 30,
      });

      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.copy(pos);
      networkGroup.add(mesh);
      nodes.push(mesh);
    }

    // C. Logistics Routes & Data Flow Packets
    const routes = [];
    const packets = [];

    const routeMaterial = new THREE.LineBasicMaterial({
      color: amberColor,
      transparent: true,
      opacity: 0.2,
    });

    const packetGeo = new THREE.SphereGeometry(0.08, 8, 8);
    const packetMat = new THREE.MeshBasicMaterial({
      color: orangeColor,
      transparent: true,
      opacity: 0.9,
    });

    // Create paths connecting each outer node to the central tower
    nodeCoords.forEach((nodePos) => {
      const start = new THREE.Vector3(0, 0, 0); // Control Tower Center
      const end = nodePos;

      // Curve path outward
      const mid = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
      mid.y += (Math.random() - 0.5) * 2; // Curve offset

      const curve = new THREE.QuadraticBezierCurve3(start, end, mid);
      const points = curve.getPoints(30);
      const routeGeo = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(routeGeo, routeMaterial);
      networkGroup.add(line);

      routes.push({ curve, line });

      // Add floating routing packets
      const packetMesh = new THREE.Mesh(packetGeo, packetMat);
      networkGroup.add(packetMesh);
      packets.push({
        mesh: packetMesh,
        curve,
        progress: Math.random(),
        speed: 0.003 + Math.random() * 0.004,
      });
    });

    // D. Golden Data Dust Particles
    const dustCount = 150;
    const dustPositions = new Float32Array(dustCount * 3);
    for (let i = 0; i < dustCount; i++) {
      dustPositions[i * 3] = (Math.random() - 0.5) * 20;
      dustPositions[i * 3 + 1] = (Math.random() - 0.5) * 15;
      dustPositions[i * 3 + 2] = (Math.random() - 0.5) * 15;
    }
    const dustGeo = new THREE.BufferGeometry();
    dustGeo.setAttribute("position", new THREE.BufferAttribute(dustPositions, 3));
    const dustMat = new THREE.PointsMaterial({
      color: goldColor,
      size: 0.05,
      transparent: true,
      opacity: 0.4,
    });
    const spaceDust = new THREE.Points(dustGeo, dustMat);
    scene.add(spaceDust);

    // 6. Mouse Parallax Hooks
    let targetRotationX = 0;
    let targetRotationY = 0;
    const handleMouseMove = (e) => {
      const x = (e.clientX - window.innerWidth / 2) / (window.innerWidth / 2);
      const y = (e.clientY - window.innerHeight / 2) / (window.innerHeight / 2);
      targetRotationY = x * 0.2;
      targetRotationX = y * 0.2;
    };
    window.addEventListener("mousemove", handleMouseMove);

    // 7. Resize Handler
    const handleResize = () => {
      if (!container) return;
      const r = container.getBoundingClientRect();
      const w = r.width || width;
      const h = r.height || height;

      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
    };
    window.addEventListener("resize", handleResize);

    // 8. Animation Loop
    let animFrameId;
    const animate = () => {
      animFrameId = requestAnimationFrame(animate);

      // Rotate network elements
      controlTower.rotation.y += 0.005;
      controlTower.rotation.x += 0.002;

      nodes.forEach((node, idx) => {
        node.rotation.y += 0.01;
        // Float nodes up and down slightly
        node.position.y = nodeCoords[idx].y + Math.sin(Date.now() * 0.001 + idx) * 0.1;
      });

      // Update Packets progress
      packets.forEach((p) => {
        p.progress += p.speed;
        if (p.progress >= 1.0) {
          p.progress = 0;
        }
        const pos = p.curve.getPointAt(p.progress);
        p.mesh.position.copy(pos);
      });

      // Slowly rotate background dust
      spaceDust.rotation.y += 0.0003;

      // Parallax rotation
      networkGroup.rotation.y += (targetRotationY - networkGroup.rotation.y) * 0.05;
      networkGroup.rotation.x += (targetRotationX - networkGroup.rotation.x) * 0.05;

      renderer.render(scene, camera);
    };
    animate();

    // 9. Cleanup
    return () => {
      cancelAnimationFrame(animFrameId);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("resize", handleResize);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      
      // Dispose resources
      towerGeo.dispose();
      towerMat.dispose();
      geometries.forEach((g) => g.dispose());
      routeMaterial.dispose();
      packetGeo.dispose();
      packetMat.dispose();
      dustGeo.dispose();
      dustMat.dispose();
      renderer.dispose();
    };
  }, [theme, orangeColor, amberColor, goldColor]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full min-h-[400px] relative overflow-hidden"
    />
  );
}
